import Grid from '@mui/material/Grid';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import type { AppInventoryOptionType, AppInventoryVersionType } from '@openx/types/targeting/appInventory.ts';
import type {
  InventoryAndContentState,
  InventoryContentOption,
  ParentAccountFeatures,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import type { AnyAction } from '@reduxjs/toolkit';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InputType, inputOptions } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { InventoryInput } from '../../../InventoryAndContent/InventoryAndContentTopBox/InventoryInput.tsx';
import { getAlertTooltipConfig } from '../../../InventoryAndContent/InventoryAndContentTopBox/utils/getAlertTooltipText.ts';
import { getItemsCount } from '../../../InventoryAndContent/InventoryAndContentTopBox/utils/getItemsCount.ts';
import type { InvalidItemsError } from '../../BulkAdd/types.ts';
import { useAppBundleIdWithInvListState } from '../hooks/useAppBundleIdWithInvListState.ts';
import { InventoryComparisonTypeDropdown } from './InventoryComparisonTypeDropdown.tsx';
import { InventoryContentOptionDropdown } from './InventoryContentOptionDropdown.tsx';

export type AppBundleIdWithInventoryListProps = {
  contentOption: InventoryContentOption | AppInventoryOptionType;
  comparisonType: ComparisonType;
  inventoryContent: InventoryAndContentState[typeof InventoryContentOption.APP_BUNDLE_ID];
  parentAccountFeatures: ParentAccountFeatures;
  accountUid?: string;
  dispatchInventory: (action: AnyAction) => void;
  setComparisonType: (type: ComparisonType) => void;
  setContentOption: ((option: InventoryContentOption) => void) | ((option: AppInventoryOptionType) => void);
  setAppBundleIdDuplicateErrors: Dispatch<SetStateAction<InvalidItemsError | null>>;
  inventoryVersion?: AppInventoryVersionType;
};

export const AppBundleIdWithInventoryList = ({
  contentOption,
  comparisonType,
  dispatchInventory,
  inventoryContent,
  setComparisonType,
  setContentOption,
  parentAccountFeatures,
  accountUid,
  setAppBundleIdDuplicateErrors,
  inventoryVersion,
}: AppBundleIdWithInventoryListProps) => {
  const {
    handleManualInput,
    handleInventoryListChange,
    errorText,
    inputType,
    setInputType,
    inventoryListsOptions,
    loading,
    mapById,
  } = useAppBundleIdWithInvListState({
    comparisonType,
    contentOption,
    dispatchInventory,
    inventoryContent,
    accountUid,
    setAppBundleIdDuplicateErrors,
  });

  const { t } = useTranslation();

  const { shouldDisplayAlert, message } = useMemo(
    () => getAlertTooltipConfig({ t, comparisonType, inventoryContent }),
    [inventoryContent, comparisonType, t]
  );

  const hasBothListAndManualInput = inventoryContent.inventorylist_id && inventoryContent?.val;

  return (
    <Grid
      container
      spacing={1}
      sx={{
        alignItems: 'flex-start',
        flexGrow: 1,
      }}
    >
      <Grid size={4}>
        <InventoryContentOptionDropdown
          value={contentOption}
          onChange={setContentOption}
          parentAccountFeatures={parentAccountFeatures}
          inventoryVersion={inventoryVersion}
        />
      </Grid>

      <Grid sx={{ mt: 2 }} size={shouldDisplayAlert ? 3 : 4}>
        <AutoComplete
          options={inputOptions}
          onChange={(_, value) => setInputType(value)}
          disableClearable
          value={inputType}
          getOptionLabel={option => (option === InputType.Manually ? t('Manually') : t('From the Inventory List'))}
          disabled={shouldDisplayAlert}
          data-test="app-bundle-id-input-type"
        />
      </Grid>

      {shouldDisplayAlert && (
        <Grid sx={{ mt: 2 }} size={1} data-test="app-bundle-id-alert-tooltip">
          <AlertTooltip icon={<InfoIcon />} color="default" title={message} />
        </Grid>
      )}

      {inputType === InputType.Manually && (
        <>
          <Grid sx={{ mt: 2 }} size={hasBothListAndManualInput ? 3 : 4}>
            <InventoryComparisonTypeDropdown
              option={contentOption}
              value={comparisonType}
              onChange={setComparisonType}
              hasInventoryList={!!inventoryContent.inventorylist_id}
            />
          </Grid>

          {hasBothListAndManualInput && (
            <Grid
              size={1}
              sx={{
                mt: 2,
              }}
            >
              <AlertTooltip
                icon={<InfoIcon />}
                color="default"
                title={t(
                  'Selecting ‘Inventory List’ makes the option ‘Do not equal’ unavailable for Manual item entry.'
                )}
              />
            </Grid>
          )}

          <Grid sx={{ mt: 0, mb: 2 }} size={12}>
            <InventoryInput
              option={contentOption}
              onKeyUp={handleManualInput}
              errorText={errorText}
              itemsCount={getItemsCount(inventoryContent.val || {})}
            />
          </Grid>
        </>
      )}

      {inputType === InputType.InventoryList && (
        <Grid size={4}>
          <AutoComplete
            textFieldProps={{
              placeholder: t('Select Inventory List'),
              label: t('Inventory List'),
            }}
            options={inventoryListsOptions}
            getOptionLabel={option => mapById[option]?.name || ''}
            onChange={handleInventoryListChange}
            disabled={loading}
            loading={loading}
            data-test={'inventory-list'}
          />
        </Grid>
      )}
    </Grid>
  );
};
